import { PrimaryTradeLicenseRequirement } from 'src/app/classes/models/primary-trade-license-requirement';
import { TradeCategory } from 'src/app/classes/models/trade-category';
import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { BaseModel } from './base-model';
import { ComplianceSetting } from './compliance-setting';
import { TradeSpecificCompliance } from './trade-specific-compliance';

export class PrimaryTrade extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static PrimaryTradeProperties: (
    | {
        property: keyof PrimaryTrade;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof PrimaryTrade;
        type: 'alias';
      }
    | {
        property: keyof PrimaryTrade;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'name', type: 'column', dataType: 'string' },
    { property: 'defaultTier', type: 'column', dataType: 'number' },
    { property: 'isTransporter', type: 'column', dataType: 'boolean' },
    { property: 'isProfessional', type: 'column', dataType: 'boolean' },
    { property: 'isNonProfessional', type: 'column', dataType: 'boolean' },
    {
      property: 'isProfessionalOrNonProfessional',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'isOnSite', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'tradeCategoriesConcatenated', type: 'alias' },
    { property: 'vendorsPrimary', type: 'alias' },
    { property: 'vendorsSecondary', type: 'alias' },
    { property: 'reqCount', type: 'alias' },

    { property: 'VendorTrades', model: 'VendorTrade', type: 'association' },
    {
      property: 'PrimaryTradeLicenseRequirements',
      model: 'PrimaryTradeLicenseRequirement',
      type: 'association'
    },
    {
      property: 'TradeCategories',
      model: 'TradeCategory',
      type: 'association'
    },
    {
      property: 'TradeSpecificCompliances',
      model: 'TradeSpecificCompliance',
      type: 'association'
    },
    {
      property: 'ComplianceSettings',
      model: 'ComplianceSetting',
      type: 'association'
    }
  ];

  static {
    PrimaryTrade.PrimaryTradeProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        PrimaryTrade.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        PrimaryTrade.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        PrimaryTrade.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(PrimaryTradeObject: Partial<PrimaryTrade>) {
    super();

    PrimaryTrade.PrimaryTradeProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = PrimaryTradeObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  name?: string;
  defaultTier?: number;
  isTransporter?: boolean;
  isProfessional?: boolean;
  isNonProfessional?: boolean;
  isProfessionalOrNonProfessional?: boolean;
  isOnSite?: boolean;
  createdAt?: Date;
  updatedAt?: Date;

  tradeCategoriesConcatenated?: string;
  vendorsPrimary?: number;
  vendorsSecondary?: number;
  reqCount?: number;

  VendorTrades?: VendorTrade[];
  PrimaryTradeLicenseRequirements?: PrimaryTradeLicenseRequirement[];
  TradeCategories?: TradeCategory[];
  TradeSpecificCompliances?: TradeSpecificCompliance[];
  ComplianceSettings?: ComplianceSetting[];
}
